interface LogEntry {
  timestamp: string;
  level: 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';
  category: string;
  message: string;
  context?: any;
  userId?: string;
  sessionId: string;
  url: string;
  userAgent: string;
}

interface PerformanceEntry {
  timestamp: string;
  action: string;
  duration: number;
  details?: any;
}

class Logger {
  private sessionId: string;
  private userId?: string;
  private logs: LogEntry[] = [];
  private performanceLogs: PerformanceEntry[] = [];

  constructor() {
    this.sessionId = this.generateSessionId();
    this.setupGlobalErrorHandling();
    this.setupPerformanceMonitoring();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private createLogEntry(
    level: LogEntry['level'],
    category: string,
    message: string,
    context?: any
  ): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      context,
      userId: this.userId,
      sessionId: this.sessionId,
      url: window.location.href,
      userAgent: navigator.userAgent
    };
  }

  setUserId(userId: string) {
    this.userId = userId;
    this.info('AUTH', 'User ID set for logging', { userId });
  }

  debug(category: string, message: string, context?: any) {
    const entry = this.createLogEntry('DEBUG', category, message, context);
    this.logs.push(entry);
    console.debug(`[${category}] ${message}`, context);
  }

  info(category: string, message: string, context?: any) {
    const entry = this.createLogEntry('INFO', category, message, context);
    this.logs.push(entry);
    console.info(`[${category}] ${message}`, context);
  }

  warn(category: string, message: string, context?: any) {
    const entry = this.createLogEntry('WARN', category, message, context);
    this.logs.push(entry);
    console.warn(`[${category}] ${message}`, context);
  }

  error(category: string, message: string, context?: any) {
    const entry = this.createLogEntry('ERROR', category, message, context);
    this.logs.push(entry);
    console.error(`[${category}] ${message}`, context);
    this.sendToBackend([entry]);
  }

  // User interaction logging
  logUserAction(action: string, details?: any) {
    this.info('USER_ACTION', `User performed: ${action}`, details);
  }

  // API call logging
  logApiCall(method: string, url: string, status?: number, duration?: number, error?: any) {
    const context = { method, url, status, duration, error };
    if (error || (status && status >= 400)) {
      this.error('API_CALL', `API call failed: ${method} ${url}`, context);
    } else {
      this.info('API_CALL', `API call successful: ${method} ${url}`, context);
    }
  }

  // Auth0 event logging
  logAuth0Event(event: string, details?: any) {
    this.info('AUTH0', `Auth0 event: ${event}`, details);
  }

  // Component lifecycle logging
  logComponentEvent(component: string, event: string, details?: any) {
    this.debug('COMPONENT', `${component}: ${event}`, details);
  }

  // Performance logging
  logPerformance(action: string, duration: number, details?: any) {
    const entry: PerformanceEntry = {
      timestamp: new Date().toISOString(),
      action,
      duration,
      details
    };
    this.performanceLogs.push(entry);
    this.info('PERFORMANCE', `${action} took ${duration}ms`, details);
  }

  // Error boundary logging
  logComponentError(component: string, error: Error, errorInfo?: any) {
    this.error('COMPONENT_ERROR', `Error in ${component}: ${error.message}`, {
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack
      },
      errorInfo
    });
  }

  private setupGlobalErrorHandling() {
    window.addEventListener('error', (event) => {
      this.error('GLOBAL_ERROR', 'Uncaught error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error
      });
    });

    window.addEventListener('unhandledrejection', (event) => {
      this.error('UNHANDLED_PROMISE', 'Unhandled promise rejection', {
        reason: event.reason
      });
    });
  }

  private setupPerformanceMonitoring() {
    // Monitor page load performance
    window.addEventListener('load', () => {
      setTimeout(() => {
        const perfData = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        if (perfData) {
          this.logPerformance('PAGE_LOAD', perfData.loadEventEnd - perfData.fetchStart, {
            domContentLoaded: perfData.domContentLoadedEventEnd - perfData.fetchStart,
            firstPaint: performance.getEntriesByName('first-paint')[0]?.startTime || 0,
            firstContentfulPaint: performance.getEntriesByName('first-contentful-paint')[0]?.startTime || 0
          });
        }
      }, 1000);
    });
  }

  // Send logs to backend for persistence (with batching and debouncing)
  private pendingLogs: LogEntry[] = [];
  private batchTimeout: NodeJS.Timeout | null = null;

  private async sendToBackend(logs: LogEntry[]) {
    // Add to pending batch
    this.pendingLogs.push(...logs);
    
    // Clear existing timeout
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
    }
    
    // Batch multiple calls into one - much longer delay during login for better UX
    this.batchTimeout = setTimeout(() => {
      this.flushPendingLogs();
    }, 3000); // Wait 3 seconds to batch multiple log calls and reduce login blocking
  }

  private async flushPendingLogs() {
    if (this.pendingLogs.length === 0) return;
    
    const logsToSend = [...this.pendingLogs];
    this.pendingLogs = [];
    this.batchTimeout = null;

    try {
      await fetch(`${process.env.REACT_APP_API_URL}/logs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ logs: logsToSend })
      });
    } catch (error) {
      console.error('Failed to send logs to backend:', error);
      // Don't retry to avoid infinite loops during authentication issues
    }
  }

  // Periodic log flushing
  flushLogs() {
    if (this.logs.length > 0) {
      this.sendToBackend([...this.logs]);
      this.logs = [];
    }
  }

  // Get logs for debugging
  getAllLogs(): LogEntry[] {
    return [...this.logs];
  }

  getPerformanceLogs(): PerformanceEntry[] {
    return [...this.performanceLogs];
  }

  // Export logs as JSON for AI analysis
  exportLogsForAI(): string {
    return JSON.stringify({
      sessionId: this.sessionId,
      userId: this.userId,
      timestamp: new Date().toISOString(),
      logs: this.logs,
      performanceLogs: this.performanceLogs,
      userAgent: navigator.userAgent,
      url: window.location.href
    }, null, 2);
  }
}

// Create global logger instance
const logger = new Logger();

// Flush logs every 30 seconds
setInterval(() => {
  logger.flushLogs();
}, 30000);

// Flush logs before page unload
window.addEventListener('beforeunload', () => {
  logger.flushLogs();
});

export default logger;